/**
 * Authentication context: the signed-in user, active team and permissions.
 */
import type { Permission } from "@shared/permissions.ts";
import type { MeResponse } from "@shared/types.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo } from "react";
import { ApiError, api, setCsrfToken, setUnauthorizedHandler } from "./api.ts";

interface AuthState {
  me: MeResponse | null;
  loading: boolean;
  can: (permission: Permission) => boolean;
  refresh: () => Promise<void>;
  setMe: (me: MeResponse | null) => void;
  logout: () => Promise<void>;
  switchTeam: (teamId: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export const ME_KEY = ["auth", "me"];

export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api.get<MeResponse>("/api/auth/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
  const me = query.data ?? null;

  useEffect(() => {
    if (me) setCsrfToken(me.csrfToken);
  }, [me]);

  const setMe = useCallback(
    (next: MeResponse | null) => {
      if (next) setCsrfToken(next.csrfToken);
      client.setQueryData(ME_KEY, next);
    },
    [client],
  );

  useEffect(() => {
    setUnauthorizedHandler(() => setMe(null));
  }, [setMe]);

  /**
   * Replaces the signed-in user and drops every other cached query (they belong to the
   * previous user/team). The "me" query itself must stay in the cache: `client.clear()`
   * would detach the observer above, leaving the old user in place after sign-out.
   */
  const resetSession = useCallback(
    (next: MeResponse | null) => {
      client.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
      setMe(next);
    },
    [client, setMe],
  );

  const value = useMemo<AuthState>(() => {
    const perms = new Set(me?.permissions ?? []);
    return {
      me,
      loading: query.isLoading,
      can: (p) => perms.has(p),
      refresh: async () => {
        await query.refetch();
      },
      setMe,
      logout: async () => {
        await api.post("/api/auth/logout").catch(() => undefined);
        resetSession(null);
      },
      switchTeam: async (teamId) => {
        const next = await api.post<MeResponse>("/api/auth/switch-team", { teamId });
        resetSession(next);
      },
    };
  }, [me, query, setMe, resetSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
